import {
  create,
  fromJson,
  toJson,
  type DescMessage,
  type JsonValue,
  type MessageShape,
} from '@bufbuild/protobuf';
import { apiClient } from './request';
import {
  CheckAgentTokenNameRequestSchema,
  CheckAgentTokenNameResponseSchema,
  CreateAgentTokenRequestSchema,
  CreateAgentTokenResponseSchema,
  DeleteAgentTokenRequestSchema,
  DeleteAgentTokenResponseSchema,
  DisableAgentTokenRequestSchema,
  DisableAgentTokenResponseSchema,
  EnableAgentTokenRequestSchema,
  EnableAgentTokenResponseSchema,
  GetAgentTokenRequestSchema,
  GetAgentTokenResponseSchema,
  ListAgentTokensResponseSchema,
  RegenerateAgentTokenRequestSchema,
  RegenerateAgentTokenResponseSchema,
  UpdateAgentTokenRequestSchema,
  UpdateAgentTokenResponseSchema,
  type CheckAgentTokenNameRequest,
  type CreateAgentTokenData,
  type CreateAgentTokenRequest,
  type DisableAgentTokenData,
  type EnableAgentTokenData,
  type ListAgentTokensData,
  type RegenerateAgentTokenData,
  type UpdateAgentTokenRequest,
} from '@/proto/agentplatform/httpv2/token_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import type { AgentToken } from '@/proto/agentplatform/v2/token_pb';

/**
 * 拆 httpv2 成功包络取业务 data。envelope 的业务错误已由 apiClient 响应拦截器
 * 统一识别并 reject 成 ApiError（见 request.ts / errors.ts），这里只处理成功分支。
 */
async function send<TResponse extends DescMessage, TData>(
  path: string,
  body: JsonValue,
  successSchema: TResponse,
  pickData: (resp: MessageShape<TResponse>) => TData | undefined
): Promise<TData> {
  const responseObject = await apiClient.post<JsonValue>(path, body);
  const raw = responseObject.data;
  const wrapped = fromJson(successSchema, raw);
  const data = pickData(wrapped);
  if (data === undefined || data === null) {
    throw new Error('empty response data');
  }
  return data;
}

export const agentTokenApi = {
  list: async (params: ListRequest): Promise<ListAgentTokensData> =>
    send(
      '/v2/ListAgentTokens',
      toJson(ListRequestSchema, params),
      ListAgentTokensResponseSchema,
      (r) => r.data
    ),

  /** 创建后响应返回明文一次（CreateAgentTokenData：id/name/token）。 */
  create: async (data: CreateAgentTokenRequest): Promise<CreateAgentTokenData> =>
    send(
      '/v2/CreateAgentToken',
      toJson(CreateAgentTokenRequestSchema, data),
      CreateAgentTokenResponseSchema,
      (r) => r.data
    ),

  /** 详情接口返回脱敏后的 token（AgentToken.token 为脱敏展示值，不含明文）。 */
  get: async (id: string): Promise<AgentToken> =>
    send(
      '/v2/GetAgentToken',
      toJson(GetAgentTokenRequestSchema, create(GetAgentTokenRequestSchema, { id })),
      GetAgentTokenResponseSchema,
      (r) => r.data
    ),

  update: async (data: UpdateAgentTokenRequest): Promise<AgentToken> =>
    send(
      '/v2/UpdateAgentToken',
      toJson(UpdateAgentTokenRequestSchema, data),
      UpdateAgentTokenResponseSchema,
      (r) => r.data
    ),

  delete: async (id: string): Promise<void> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/DeleteAgentToken',
      toJson(DeleteAgentTokenRequestSchema, create(DeleteAgentTokenRequestSchema, { id }))
    );
    // envelope 业务错误已在拦截器 reject；Delete 幂等，成功/不存在都当成功。
    fromJson(DeleteAgentTokenResponseSchema, responseObject.data); // 仅做格式校验
  },

  /** 重新生成：旧 token 立即失效，响应返回新明文一次（RegenerateAgentTokenData）。 */
  regenerate: async (id: string): Promise<RegenerateAgentTokenData> =>
    send(
      '/v2/RegenerateAgentToken',
      toJson(RegenerateAgentTokenRequestSchema, create(RegenerateAgentTokenRequestSchema, { id })),
      RegenerateAgentTokenResponseSchema,
      (r) => r.data
    ),

  enable: async (id: string): Promise<EnableAgentTokenData> =>
    send(
      '/v2/EnableAgentToken',
      toJson(EnableAgentTokenRequestSchema, create(EnableAgentTokenRequestSchema, { id })),
      EnableAgentTokenResponseSchema,
      (r) => r.data
    ),

  disable: async (id: string): Promise<DisableAgentTokenData> =>
    send(
      '/v2/DisableAgentToken',
      toJson(DisableAgentTokenRequestSchema, create(DisableAgentTokenRequestSchema, { id })),
      DisableAgentTokenResponseSchema,
      (r) => r.data
    ),

  /** 创建前预检：名称在所属范围内是否可用（API→同一 agent 实例；WS→同一渠道账号）。 */
  checkName: async (data: CheckAgentTokenNameRequest): Promise<boolean> =>
    send(
      '/v2/CheckAgentTokenName',
      toJson(CheckAgentTokenNameRequestSchema, data),
      CheckAgentTokenNameResponseSchema,
      (r) => r.data?.available
    ),
};
